import { ref, onMounted } from 'vue';
import { wechatService } from '../api/wechatService';
import { useAuthStore } from '@/auth/store/authStore';

/**
 * 微信JS-SDK相关功能的组合式函数
 * @param {Object} options 配置选项
 * @param {boolean} options.autoConfig 是否自动配置微信JS-SDK
 * @param {Array<string>} options.jsApiList 需要使用的JS接口列表
 * @returns {Object} 微信相关方法和状态
 */
export function useWechat(options = {}) {
  const {
    autoConfig = true,
    jsApiList = ['updateAppMessageShareData', 'updateTimelineShareData']
  } = options;
  
  const loading = ref(false);
  const error = ref(null);
  const isConfigured = ref(false);
  
  /**
   * 配置微信JS-SDK
   * @param {string} url 当前页面URL，默认为当前浏览器URL
   * @returns {Promise<boolean>} 配置是否成功
   */
  const configJssdk = async (url = window.location.href) => {
    if (typeof window.wx === 'undefined') {
      console.error('[useWechat] 微信JS-SDK未加载');
      error.value = '微信JS-SDK未加载';
      return false;
    }
    
    // 重置配置状态，以便重新配置
    isConfigured.value = false;
    loading.value = true;
    error.value = null;
    console.log('[useWechat] 开始配置 JS-SDK...');
    
    // 在尝试请求签名之前获取认证状态
    const authStore = useAuthStore();

    try {
      const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
      const isMiniProgram = window.__wxjs_environment === 'miniprogram'; // 检测小程序环境
      
      // 清理URL，移除哈希和不必要的查询参数
      let cleanUrl = url;
      if (url.includes('#')) {
        cleanUrl = url.split('#')[0];
      }
      
      // **更彻底的 URL 清理**
      const urlObj = new URL(cleanUrl);
      urlObj.searchParams.delete('code');
      urlObj.searchParams.delete('state');
      // 添加其他可能由微信或小程序注入的参数
      urlObj.searchParams.delete('appid');
      urlObj.searchParams.delete('sessionid');
      urlObj.searchParams.delete('pass_ticket');
      urlObj.searchParams.delete('from'); // 常见的分享来源参数
      urlObj.searchParams.delete('isappinstalled'); // 微信可能添加的参数
      
      let finalUrlForSignature = urlObj.toString();
      // 确保 URL 结尾没有斜杠 (如果后端签名时也没有)
      // if (finalUrlForSignature.endsWith('/')) {
      //    finalUrlForSignature = finalUrlForSignature.slice(0, -1);
      // }
      // 或者确保 URL 结尾有斜杠 (如果后端签名时有)
      // if (!finalUrlForSignature.endsWith('/')) {
      //    finalUrlForSignature += '/';
      // }
      // **注意**: 上面处理尾部斜杠的逻辑需要根据你后端签名服务的要求来定，保持一致性！
      
      console.log(`[useWechat] 准备请求签名 - 原始URL: ${url}`);
      console.log(`[useWechat] 准备请求签名 - 清理后URL: ${finalUrlForSignature}`);
      console.log(`[useWechat] 环境检测 - iOS: ${isIOS}, 小程序: ${isMiniProgram}`);
      
      // --- 添加认证检查 ---
      // 如果用户未登录，则跳过需要签名的 JSSDK 配置部分
      if (!authStore.isAuthenticated) {
        console.warn('[useWechat] 用户未登录，跳过需要认证的 JSSDK 配置 (签名获取)');
        isConfigured.value = false; // 标记为未配置（因为需要签名的部分无法完成）
        // 注意：这里不设置 error.value，因为未登录不是一个错误状态，只是不能执行特定操作
        loading.value = false;
        // 返回 false 表示配置未成功（对于需要签名的功能而言）
        return false;
      }
      // --- 认证检查结束 ---
      
      // 调用签名服务 (只有在用户已认证时才会执行到这里)
      console.log(`[useWechat] 用户已认证，准备请求签名`);
      const signatureInfo = await wechatService.getJssdkSignature(finalUrlForSignature);
      
      if (!signatureInfo) {
         console.error('[useWechat] 获取签名信息失败，后端未返回有效签名。');
         error.value = '获取微信签名失败';
         isConfigured.value = false;
         return false;
      }
      console.log('[useWechat] 获取到签名信息:', { appId: signatureInfo.appId, timestamp: signatureInfo.timestamp, nonceStr: signatureInfo.nonceStr, signature: '...' }); // 不打印完整签名
      
      // 调用 wx.config，并使用 Promise 包装 ready 和 error 回调
      return new Promise((resolve) => {
          console.log('[useWechat] 调用 wx.config...');
          window.wx.config({
            debug: process.env.NODE_ENV === 'development', // 开发环境开启 debug
            appId: signatureInfo.appId,
            timestamp: signatureInfo.timestamp,
            nonceStr: signatureInfo.nonceStr,
            signature: signatureInfo.signature,
            jsApiList: jsApiList, // 使用传入的 jsApiList
            openTagList: ['wx-open-launch-weapp'] // 如果用到开放标签，需要在这里也声明
          });
          
          window.wx.ready(() => {
            console.log('[useWechat] wx.config 配置成功 (wx.ready 触发)');
            isConfigured.value = true;
            error.value = null; // 配置成功，清除之前的错误状态
            resolve(true);
          });
          
          window.wx.error((res) => {
            console.error('[useWechat] wx.config 配置失败 (wx.error 触发):', res);
            // 记录详细错误信息
            error.value = `wx.config 错误: ${res.errMsg}`;
            isConfigured.value = false;
            // **关键**: 打印配置失败时的 URL 和签名信息，便于比对
            console.error(`[useWechat] wx.config 失败详情 - URL: ${finalUrlForSignature}`);
            console.error(`[useWechat] wx.config 失败详情 - AppId: ${signatureInfo.appId}`);
            console.error(`[useWechat] wx.config 失败详情 - Timestamp: ${signatureInfo.timestamp}`);
            console.error(`[useWechat] wx.config 失败详情 - NonceStr: ${signatureInfo.nonceStr}`);
            console.error(`[useWechat] wx.config 失败详情 - Signature: ${signatureInfo.signature}`);
            resolve(false);
          });
          
      }).catch(configError => {
          console.error('[useWechat] wx.config 调用时发生同步错误:', configError);
          error.value = 'wx.config 调用异常';
          isConfigured.value = false;
          return false; // 确保返回 boolean
      });
      
    } catch (err) {
      console.error('[useWechat] configJssdk 内部发生错误:', err);
      error.value = err.message || '配置微信JS-SDK时发生异常';
      isConfigured.value = false;
      return false;
    } finally {
      loading.value = false;
      console.log('[useWechat] 结束配置 JS-SDK');
    }
  };
  
  /**
   * 获取微信JS-SDK签名
   * @param {string} url 当前页面URL，默认为当前浏览器URL
   * @returns {Promise<Object>} 签名信息
   */
  const getSignature = async (url = window.location.href) => {
    loading.value = true;
    error.value = null;
    
    try {
      const result = await wechatService.getJssdkSignature(url);
      if (!result) {
        error.value = '获取微信签名失败';
      }
      return result;
    } catch (err) {
      error.value = err.message || '获取微信签名失败';
      return null;
    } finally {
      loading.value = false;
    }
  };
  
  /**
   * 设置微信分享信息
   * @param {Object} shareData 分享数据
   * @param {string} shareData.title 分享标题
   * @param {string} shareData.desc 分享描述
   * @param {string} shareData.link 分享链接
   * @param {string} shareData.imgUrl 分享图标
   * @returns {boolean} 设置是否成功
   */
  const setShareInfo = (shareData) => {
    if (typeof window.wx === 'undefined' || !isConfigured.value) {
      error.value = '微信JS-SDK未配置';
      return false;
    }
    
    try {
      // 分享给朋友
      window.wx.updateAppMessageShareData({
        title: shareData.title,
        desc: shareData.desc,
        link: shareData.link,
        imgUrl: shareData.imgUrl,
        success: function() {
          console.log('设置分享朋友成功');
        }
      });
      
      // 分享到朋友圈
      window.wx.updateTimelineShareData({
        title: shareData.title,
        link: shareData.link,
        imgUrl: shareData.imgUrl,
        success: function() {
          console.log('设置分享朋友圈成功');
        }
      });
      
      return true;
    } catch (err) {
      error.value = err.message || '设置分享信息失败';
      return false;
    }
  };
  
  // 组件挂载时自动配置微信JS-SDK
  onMounted(() => {
    if (autoConfig && typeof window.wx !== 'undefined') {
      configJssdk();
    }
  });
  
  return {
    // 状态
    loading,
    error,
    isConfigured,
    
    // 方法
    configJssdk,
    getSignature,
    setShareInfo,
    
    // 服务
    wechatService
  };
} 